import { useState } from 'react';
import './ResumeAttachment.css';
import { supabase } from './supabaseClient';

export default function ResumeAttachment({ url, name, applicantName = 'applicant', sample = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const storagePath = !sample && typeof url === 'string' && url && !/^[a-z][a-z0-9+.-]*:|^\//i.test(url) ? url : null;
  let safeUrl;
  try {
    const parsed = new URL(url, window.location.origin);
    if (url && ['https:', 'http:'].includes(parsed.protocol)) safeUrl = parsed.href;
  } catch { /* An unavailable attachment must not become an unsafe link. */ }
  if (!safeUrl && !storagePath) return <p className="resume-empty">No resume attached</p>;
  const filename = Array.from(name || 'application-resume', character => character.charCodeAt(0) < 32 || '\\/:*?"<>|'.includes(character) ? '_' : character).join('');
  const localFile = !storagePath && new URL(safeUrl).origin === window.location.origin;
  const download = async (open = false) => {
    if (busy) return;
    setBusy(true); setError(false);
    const preview = open ? window.open('about:blank', '_blank') : null;
    if (preview) preview.opener = null;
    let objectUrl;
    let stage = 'request';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
      if (open && !preview) throw new Error('Preview blocked');
      if (preview && storagePath) {
        const { data, error: accessError } = await supabase.storage.from('resumes').createSignedUrl(storagePath, 60);
        if (accessError || !data?.signedUrl) throw new Error('Preview unavailable');
        preview.location.href = data.signedUrl;
        return;
      }
      let file;
      if (storagePath) {
        const result = await supabase.storage.from('resumes').download(storagePath, {}, { signal: controller.signal, cache: 'no-store' });
        if (result.error || !result.data) throw new Error('Attachment unavailable');
        file = result.data;
      } else {
        const response = await fetch(safeUrl, { credentials: 'omit', referrerPolicy: 'no-referrer', signal: controller.signal });
        if (!response.ok) throw new Error('Download unavailable');
        file = await response.blob();
      }
      stage = 'file';
      if (!file.size) throw new Error('Empty attachment');
      stage = 'download';
      objectUrl = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      const completedUrl = objectUrl;
      setTimeout(() => URL.revokeObjectURL(completedUrl), 60000);
    } catch (failure) {
      console.warn('Resume download unavailable', stage, failure?.name || 'Error');
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (preview) preview.close();
      setError(true);
    } finally { clearTimeout(timeout); setBusy(false); }
  };
  return <div className="resume-attachment">
    <span className="resume-label">{sample ? 'Fictional sample resume' : 'Application resume'}</span>
    {name && <span className="resume-filename">{name}</span>}
    {/\.docx?$/i.test(name || storagePath || '') && <span className="resume-filename">Word attachment - opens in a compatible app; not a PDF preview.</span>}
    <div className="resume-actions">
      <>{localFile ? <a className="resume-download" href={safeUrl} download={filename} aria-label={`Download resume for ${applicantName}`}>Download resume</a> : <button type="button" disabled={busy} onClick={() => download()} aria-label={`Download resume for ${applicantName}`}>{busy ? 'Downloading…' : 'Download resume'}</button>}</>
      {storagePath ? <button type="button" disabled={busy} onClick={() => download(true)} aria-label={`Open resume for ${applicantName}`}>Open resume</button> : <a href={safeUrl} target="_blank" rel="noopener noreferrer" aria-label={`Open resume for ${applicantName}`}>Open resume</a>}
    </div>
    {error && <p role="alert">Resume unavailable. The file may be missing or access may have changed. If a preview was blocked, try Download resume.</p>}
  </div>;
}
